/**
 * Подгряване на огледалото при вход (§14.6 №5 и §16.4 №3 на договора,
 * docs/edge.md §Огледало). Касата сервира без интернет САМО това, което е
 * видяла през `PUT {edge}/mirror`, а огледалото се пълни от отговорите на
 * самото приложение: екран, който никой не е отварял, докато е имало
 * интернет, липсва. Затова при вход с включен резерв се прави по един ТИХ
 * `GET` за белите пътища, които касата още не е виждала — иначе първият
 * офлайн час е с празен списък клиенти („Клиенти"/„Отстъпки"), без методи на
 * плащане, без сервитьори за „Прехвърли" и с кухненски монитор, който не
 * знае станциите си.
 *
 * Правила: само в режим `cloud` и само с билет; пропуска всичко, което вече
 * е в `GET {edge}/status`.`mirror` или е било изтеглено успешно в това
 * зареждане; грешките (липсващо право, стар BE) се гълтат — подгряването
 * няма право да забави или да счупи входа. Затова път, отказан на един
 * потребител (напр. терминалният акаунт без `select_customer`), се опитва
 * пак при следващия вход — със СЛЕДВАЩИЯ човек правата може да стигат.
 * Огледалният `PUT` към касата го прави response интерсепторът на
 * `api/client.ts`, както за всеки друг GET.
 */
import { api } from "../../api/client";
import { getEdgeStatus } from "../../api/edge";
import { RESERVATIONS_EDGE_VIEW_PATH } from "../../api/reservations";
import { getRestaurantName } from "../restaurant";
import { devLog, getActiveEdgeTicket, getEdgeRuntime } from "./edgeRuntime";
import {
  getEdgeBaseUrl,
  getEdgeConfigState,
  isEdgeLocalMode,
} from "./edgeState";

interface WarmupRequest {
  path: string;
  params?: Record<string, string>;
}

// Колко чакаме Redux да научи кой е влязъл и през колко питаме: пътят на
// монитора носи неговото ид, а подгряването тръгва успоредно с
// `GET /user/info` и понякога го изпреварва (§16.4 №3).
const IDENTITY_WAIT_MS = 10_000;
const IDENTITY_POLL_MS = 500;

/** Пътищата, които касата ще трябва да сервира, а приложението може и да не
 * поиска, преди интернетът да падне. Всички са в белите префикси на
 * `edgeMirror.ts` (префикси или точни пътища) — иначе отговорът не би
 * стигнал до касата. */
const warmupRequests = (): WarmupRequest[] => {
  const restaurantName = getRestaurantName();
  return [
    { path: "/discounts/clients", params: { restaurant_name: restaurantName } },
    { path: "/payment-methods" },
    { path: "/user/users" },
    { path: "/menu/", params: { restaurant_name: restaurantName } },
    // Резервациите без интернет — само срещу каса, която ги умее (по-стара
    // каса би ги пазила като безполезен ред). После ги опреснява
    // `edgeReservationsFeed.ts`; тук е първото пълнене след вход.
    ...(reservationsOfflineNow()
      ? [{ path: RESERVATIONS_EDGE_VIEW_PATH }, { path: "/restaurant-schema" }]
      : []),
  ];
};

const reservationsOfflineNow = (): boolean =>
  getEdgeRuntime()?.store.getState().edge.reservationsOffline === true;

/**
 * Кухненският/барският екран (§16.4 №3). `GET /monitors/info/{uid}` е първото,
 * което `pages/monitors` иска, и без огледало на този път екранът показва
 * „Недостъпно без интернет": станциите му са неизвестни, значи не тръгва нито
 * един станционен WebSocket. Дотук пътят се огледваше само ако мониторът е бил
 * отварян, докато е имало интернет.
 *
 * Той е единственият бял път с ид в адреса, затова се сглобява отделно — чак
 * когато Redux знае кой е влязъл. Терминал без самоличност (общият акаунт X
 * преди ПИН-а) го пропуска: мониторът работи под свой акаунт и подгрява своя
 * път при неговия вход.
 */
async function monitorWarmupRequest(): Promise<WarmupRequest | null> {
  const userId = await waitForLoggedInUserId();
  return userId === null ? null : { path: `/monitors/info/${userId}` };
}

async function waitForLoggedInUserId(): Promise<number | null> {
  const runtime = getEdgeRuntime();
  if (!runtime) return null;
  const until = Date.now() + IDENTITY_WAIT_MS;
  for (;;) {
    const userId = runtime.store.getState().auth.userId;
    if (userId !== null) return userId;
    if (Date.now() >= until) return null;
    await new Promise((resolve) =>
      window.setTimeout(resolve, IDENTITY_POLL_MS),
    );
  }
}

// Един полет наведнъж (сесиите тръгват при всяка смяна на токена) и памет
// за вече подгрятото в ТОВА зареждане.
let warmupInFlight = false;
const warmedPaths = new Set<string>();

/** Касата вече има отговор за този път (ключът е пътят, със или без query). */
const isMirrored = (
  mirror: Record<string, string> | undefined,
  path: string,
): boolean =>
  !!mirror &&
  Object.keys(mirror).some((key) => key === path || key.startsWith(`${path}?`));

export async function warmEdgeMirror(): Promise<void> {
  if (warmupInFlight || isEdgeLocalMode() || !getEdgeConfigState()?.enabled) {
    return;
  }
  const base = getEdgeBaseUrl();
  const ticket = getActiveEdgeTicket();
  if (!base || !ticket) return;

  warmupInFlight = true;
  try {
    let mirror: Record<string, string> | undefined;
    try {
      mirror = (await getEdgeStatus(base, ticket)).mirror;
    } catch (error) {
      // Касата не отговаря сега — при следващия вход опитваме пак.
      devLog("mirror warmup skipped — no status from the till", error);
      return;
    }

    for (const request of warmupRequests()) await warmPath(request, mirror);
    // Пътят на монитора е последен: чакането за самоличността (до 10 s на общ
    // терминал, където тя идва чак с ПИН-а) не бива да бави останалите.
    const monitorRequest = await monitorWarmupRequest();
    if (monitorRequest) await warmPath(monitorRequest, mirror);
  } finally {
    warmupInFlight = false;
  }
}

/** Един тих `GET` през облака, ако касата още не е виждала пътя. */
async function warmPath(
  request: WarmupRequest,
  mirror: Record<string, string> | undefined,
): Promise<void> {
  if (warmedPaths.has(request.path)) return;
  // Режимът може да се е сменил, докато е текло чакането за самоличността —
  // в локален режим няма какво да се огледва (четенето идва от касата).
  if (isEdgeLocalMode()) return;
  if (isMirrored(mirror, request.path)) {
    warmedPaths.add(request.path);
    return;
  }
  try {
    await api.get(request.path, { params: request.params });
    warmedPaths.add(request.path);
    devLog("mirror warmup", request.path);
  } catch (error) {
    // Липсващо право или стар BE: пътят остава за следващия вход.
    devLog("mirror warmup failed", request.path, error);
  }
}
